import React from 'react';
import { Box } from '@mui/material';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

// Register the required Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * OutcomeDistributionChart component for displaying distribution of test executions by outcome
 * @param {Object} props - Component props
 * @param {Array} props.testExecutions - Array of test execution objects
 * @returns {JSX.Element} OutcomeDistributionChart component
 */
const OutcomeDistributionChart = ({ testExecutions = [] }) => {
  // Count tests by outcome
  const outcomeCount = testExecutions.reduce((acc, test) => {
    const outcome = test.outcome || 'Not Run';
    acc[outcome] = (acc[outcome] || 0) + 1;
    return acc;
  }, {});
  
  // Extract outcomes and counts
  const outcomes = Object.keys(outcomeCount);
  const counts = Object.values(outcomeCount);
  
  // Define colors for each outcome
  const outcomeColors = {
    'Passed': '#4caf50', // Green
    'Failed': '#f44336', // Red
    'Not Run': '#9e9e9e', // Grey
    'Blocked': '#ff9800', // Orange
    'Not Applicable': '#2196f3', // Blue
    'Inconclusive': '#673ab7', // Purple
    'Not Executed': '#795548' // Brown
  };
  
  // Get colors for each outcome
  const colors = outcomes.map(outcome => outcomeColors[outcome] || '#9e9e9e');
  
  // Chart data
  const chartData = {
    labels: outcomes,
    datasets: [
      {
        data: counts,
        backgroundColor: colors,
        borderColor: colors.map(() => 'transparent'),
        borderWidth: 1,
        hoverOffset: 4
      }
    ]
  };
  
  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Test Outcomes Distribution',
        font: {
          size: 16
        }
      },
      legend: {
        position: 'right',
        labels: {
          boxWidth: 15,
          padding: 15
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };
  
  // If no data, display a message
  if (outcomes.length === 0) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        height="100%"
      >
        No outcome data available
      </Box>
    );
  }
  
  return (
    <Box height="300px" width="100%" overflow="hidden">
      <Pie data={chartData} options={options} />
    </Box>
  );
};

export default OutcomeDistributionChart;
